'use client';
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';

export default function useEntitlements(uid) {
  const [ent, setEnt] = useState(null);
  // ✅ ADDED: Start in loading state
  const [loading, setLoading] = useState(true);
  
  const { firebaseUser } = useAuth(); 

  useEffect(() => {
    if (!uid) { 
      setEnt(null); 
      setLoading(false); 
      return; 
    }
    
    setLoading(true);

    const ref = doc(db, 'users', uid, 'entitlements', 'app');
    const unsub = onSnapshot(ref, (s) => {
      setEnt(s.data() || { plan: 'free', features: {} });
      // ✅ FINISHED LOADING
      setLoading(false);
    }, (err) => {
      console.error(err);
      setLoading(false);
    });

    return () => unsub();
  }, [uid]);

  const FOUNDER_CUTOFF_DATE = new Date('2026-03-01T00:00:00');
  const creationTime = firebaseUser?.metadata?.creationTime
    ? new Date(firebaseUser.metadata.creationTime)
    : new Date();

  const isFounder = creationTime < FOUNDER_CUTOFF_DATE;

  const plan = ent?.plan ?? 'free';
  const features = ent?.features ?? {};

  // Legacy: 'pro' maps to 'coach' for backwards compat
  const normalizedPlan = plan === 'pro' ? 'coach' : plan;

  // ---------------------------------------------------------------------
  // Phase 1 override (per docs/pricing-strategy.md):
  //   First 90 days we run founding clubs on manual billing with no plan
  //   gates, no upgrade CTAs, and no metering. Every authenticated user
  //   is treated as if they were on the top tier so feature gates stop
  //   gating and upgrade banners stop rendering. When Phase 3 rolls
  //   around (self-serve Stripe + per-tier enforcement), delete this
  //   block and restore the original logic below.
  // ---------------------------------------------------------------------
  const PHASE_1_UNLOCK_ALL = true;

  const isCoach = PHASE_1_UNLOCK_ALL
    ? true
    : ['coach', 'club'].includes(normalizedPlan) || isFounder;
  const isClub = PHASE_1_UNLOCK_ALL
    ? true
    : normalizedPlan === 'club';
  // Keep isPro for any code still referencing it
  const isPro = isCoach;

  return {
    plan: normalizedPlan,
    isPro,
    isCoach,
    isClub,
    isFounder,
    features,
    entitlements: ent,
    loading,
    // True while the everyone-is-top-tier launch flag is on. Lets UI
    // suppress tier BADGES (a "PRO" pill on every free signup is
    // meaningless and sets up a Phase 3 "why did I lose PRO?" moment)
    // while feature gates stay unlocked. (2026-07-24 sidebar audit.)
    phase1UnlockAll: PHASE_1_UNLOCK_ALL,
  };
}